import React from 'react';
import { Formik } from 'formik';

import { STATUSES } from 'constants/common';

import { getTranslation } from 'helpers/getTranslation';
import { getLessonStatusContextItems } from 'helpers/getLessonStatusContextItems';
import { getReadonlyLessonText } from 'helpers/getReadonlyLessonText';
import { isLessonSaveVisible } from 'helpers/isLessonSaveVisible';

import { AutoSave } from 'components/AutoSave';
import { LessonHeader } from 'components/LessonHeader';
import { LessonLayout } from 'components/Layout';
import { LessonLocaleTabs } from 'components/LessonLocaleTabs';
import { Loading } from 'components/shared/Loading';
import { ToolbarActions } from 'components/ToolbarActions';
import { ReadOnlySection } from 'components/ReadOnlySection';
import { BreadcrumbsToolbar } from 'components/Toolbar';

import { Questions } from './components/Questions';

import { convertDataToForm } from './QuizPage.helpers';

import styles from './QuizPage.module.scss';

const QuizPage = ({
  saveLoading,
  autoSaveLoading,
  readOnly,
  baseLesson,
  lesson,
  lastActionAt,
  courseTitle,
  courseStatus,
  isCatalogLesson,
  onAutoSaveSubmit,
  onSaveClick,
  onPreviewClick,
  onRemoveClick,
  onStatusContextItemClick,
  activeLocale,
  availableLocales,
  localesWithContent,
  localeContentLoading,
  lessonType,
  onLocaleChange,
}: {
  saveLoading: any;
  autoSaveLoading: any;
  readOnly: any;
  baseLesson: any;
  lesson: any;
  lastActionAt: any;
  courseTitle: any;
  courseStatus: any;
  isCatalogLesson: any;
  onAutoSaveSubmit: any;
  onSaveClick: any;
  onPreviewClick: any;
  onRemoveClick: any;
  onStatusContextItemClick: any;
  activeLocale: string;
  availableLocales: readonly string[];
  localesWithContent: string[];
  localeContentLoading: boolean;
  lessonType: string;
  onLocaleChange: (locale: string) => void;
}) => {
  const shellLesson = baseLesson;

  if (localeContentLoading || !lesson) {
    return (
      <LessonLayout
        sidebarComponent={<div />}
        toolbarComponent={
          <BreadcrumbsToolbar
            breadcrumbsData={{
              courseTitle,
              lessonTitle: getTranslation('common.text.untitled'),
            }}
            endAdornment={
              <ToolbarActions
                withRemove
                withSave={isLessonSaveVisible({
                  status: shellLesson.status,
                  activeLocale,
                })}
                saveLoading={saveLoading}
                autoSaveLoading={autoSaveLoading}
                contextItems={getLessonStatusContextItems({
                  courseReadOnly:
                    isCatalogLesson || courseStatus === STATUSES.PUBLISHED,
                  status: shellLesson.status,
                })}
                removeData={{
                  id: shellLesson.id,
                  title: shellLesson.title,
                }}
                updatedAt={lastActionAt}
                status={shellLesson.status}
                onSaveClick={() => {
                  return;
                }}
                onPreviewClick={onPreviewClick}
                onRemoveClick={onRemoveClick}
                onStatusContextItemClick={() => {
                  return;
                }}
              />
            }
          />
        }
      >
        <div className={styles.page}>
          {readOnly && (
            <ReadOnlySection text={getReadonlyLessonText(shellLesson.status)} />
          )}
          <LessonLocaleTabs
            lessonId={shellLesson.id}
            lessonType={lessonType}
            activeLocale={activeLocale}
            availableLocales={availableLocales}
            localesWithContent={localesWithContent}
            onLocaleChange={onLocaleChange}
          />
          <Loading />
        </div>
      </LessonLayout>
    );
  }

  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={convertDataToForm(lesson, readOnly)}
      onSubmit={onAutoSaveSubmit}
    >
      {({ values, setFieldValue }: { values: any; setFieldValue: any }) => (
        <LessonLayout
          sidebarComponent={<div />}
          toolbarComponent={
            <BreadcrumbsToolbar
              breadcrumbsData={{
                courseTitle,
                lessonTitle:
                  values.title || getTranslation('common.text.untitled'),
              }}
              endAdornment={
                <ToolbarActions
                  withRemove
                  withSave={isLessonSaveVisible({
                    status: shellLesson.status,
                    activeLocale,
                  })}
                  saveLoading={saveLoading}
                  autoSaveLoading={autoSaveLoading}
                  contextItems={getLessonStatusContextItems({
                    courseReadOnly:
                      isCatalogLesson || courseStatus === STATUSES.PUBLISHED,
                    status: lesson.status,
                  })}
                  removeData={{ id: lesson.id, title: lesson.title }}
                  updatedAt={lastActionAt}
                  status={lesson.status}
                  onSaveClick={() => onSaveClick(values)}
                  onPreviewClick={onPreviewClick}
                  onRemoveClick={onRemoveClick}
                  onStatusContextItemClick={(item: any) =>
                    onStatusContextItemClick(item, values, { setFieldValue })
                  }
                />
              }
            />
          }
        >
          <div className={styles.page}>
            <AutoSave debounceMs={1000} />
            {readOnly && (
              <ReadOnlySection text={getReadonlyLessonText(lesson.status)} />
            )}
            <LessonHeader readOnly={readOnly} lessonRole={lesson.role} />
            <LessonLocaleTabs
              lessonId={lesson.id}
              lessonType={lessonType}
              activeLocale={activeLocale}
              availableLocales={availableLocales}
              localesWithContent={localesWithContent}
              onLocaleChange={onLocaleChange}
            />
            <Questions />
          </div>
        </LessonLayout>
      )}
    </Formik>
  );
};

export default React.memo(QuizPage) as any;
